import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import clsx from "clsx";
import FuzzySearch from "fuzzy-search";
import { isEqual } from "lodash";

import { RowVirtualizerDynamic } from "@components/virtualized-list";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";

import { type OptionType } from "./select";
import { createSearchHighlightedLabel, renderChevronAnimation } from "./utils";

interface MultiSelectProps<Value> {
  className?: string;
  defaultValue?: OptionType<Value>["value"][];
  formatDisplayValue?: (option?: OptionType<Value>[]) => string;
  hideCaret?: boolean;
  icon?: ReactNode;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  label?: string | React.ReactElement;
  maxWidth?: string;
  name?: string;
  onChange: (value: OptionType<Value>["value"][]) => void;
  options: OptionType<Value>[];
  placeholder?: string;
  tabIndex?: number | string;
  value: OptionType<Value>["value"][];
  width?: string;

  // TODO: Determine to add or delete these props
  errorMessage?: string;
  errors?: any;

  menuItemRender?: ({
    option,
    value,
    onChange,
    active,
    selected,
  }: {
    option: OptionType<Value>;
    value: OptionType<Value>["value"][];
    onChange: (value: OptionType<Value>["value"][]) => void;
    active: boolean;
    selected: boolean;
  }) => React.ReactElement;
  menuClassName?: string;
}

const MAX_NUMBER = 1000;

export function MultiSelect<Value = unknown>({
  className,
  defaultValue,
  formatDisplayValue,
  hideCaret = false,
  icon,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  isSearchable = false,
  label,
  maxWidth,
  menuClassName,
  menuItemRender,
  name,
  onChange,
  options,
  placeholder = "Select...",
  tabIndex,
  value,
  width,
}: MultiSelectProps<Value>) {
  const searcher = new FuzzySearch(options, ["label"]);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<null | HTMLInputElement>(null);
  const foundOptions = (value
    ?.map((val) => options.find((option) => isEqual(option.value, val)))
    .filter(Boolean) ?? []) as OptionType<Value>[];
  const displayValue =
    formatDisplayValue?.(foundOptions) ??
    foundOptions.map((option) => option?.label).join(", ");
  const getDisplayValue = (open: boolean) => {
    if (isSearchable && open) {
      return null;
    }
    return displayValue || placeholder;
  };

  const filteredOptions = searcher.search(search) as OptionType<Value>[];

  useEffect(() => {
    setSearch("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [value]);

  const MultiSelectRow = useCallback(
    ({ row: option }: { row: any }) => {
      return (
        <ComboboxOption
          key={JSON.stringify(option.value)}
          value={option.value}
          disabled={option.disabled}
          as={Fragment}
        >
          {({ active, selected }) =>
            menuItemRender ? (
              menuItemRender({
                option,
                value,
                onChange,
                active,
                selected,
              })
            ) : (
              <div
                className={clsx(
                  (active || selected) && !option.disabled
                    ? "bg-cosmic-3 font-semibold"
                    : "hover:bg-cosmic-4",
                  option.disabled && "bg-gray-3 pointer-events-none",
                  "px-4 py-2 cursor-pointer text-interstellar font-normal",
                )}
                dangerouslySetInnerHTML={createSearchHighlightedLabel(
                  option.label,
                  search,
                )}
              />
            )
          }
        </ComboboxOption>
      );
    },
    [menuItemRender, onChange, search, value],
  );

  return (
    <div
      className={clsx("nb-input-container", {
        "pointer-events-none": isLoading || isDisabled,
      })}
      style={{ width, maxWidth }}
      tabIndex={Number(tabIndex) || undefined}
    >
      {label && <label className="label">{label}</label>}
      <Combobox
        multiple
        name={name}
        disabled={isDisabled}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
      >
        {({ open }) => (
          <>
            <ComboboxButton
              className={clsx(
                "nb-input flex justify-between items-center bg-white cursor-pointer",
                {
                  "border border-cosmic ring-1 ring-cosmic": open,
                  "opacity-60": isDisabled,
                },
                className,
              )}
              as="div"
            >
              <div
                className={clsx(
                  "text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm",
                  "flex-grow text-left",
                  !displayValue && placeholder && "text-disabled",
                )}
              >
                <ComboboxInput
                  className={clsx("bg-transparent", {
                    "w-full": isSearchable && open,
                    "w-0": !isSearchable || !open,
                  })}
                  value={search}
                  onChange={({ target }) => setSearch(target.value)}
                />
                {getDisplayValue(open)}
              </div>
              {isClearable && value?.length > 0 && (
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-times cursor-pointer",
                    "hover:text-cosmic px-0.5 py-2",
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onChange([]);
                  }}
                />
              )}
              {!hideCaret && (
                <span
                  className={clsx(
                    { "text-cosmic+1": open },
                    "pl-2 justify-self-end",
                  )}
                >
                  {renderChevronAnimation(open, isLoading, icon)}
                </span>
              )}
            </ComboboxButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              afterLeave={() => setSearch("")}
            >
              <ComboboxOptions
                className={clsx(
                  "absolute overflow-y-auto z-10 mt-2 px-0 py-1 bg-white rounded-lg elevation-1",
                  "text-sm",
                  menuClassName ? menuClassName : "w-full max-h-[20rem] ",
                )}
              >
                {filteredOptions.length === 0 && (
                  <div className="px-4 py-2 text-disabled">
                    NO RESULTS FOUND
                  </div>
                )}
                {filteredOptions.length > MAX_NUMBER && (
                  <div className="min-h-[19rem] h-full">
                    <RowVirtualizerDynamic
                      rows={filteredOptions}
                      Row={MultiSelectRow}
                    />
                  </div>
                )}
                {filteredOptions.length <= MAX_NUMBER &&
                  filteredOptions.map((option) => (
                    <ComboboxOption
                      key={JSON.stringify(option.value)}
                      value={option.value}
                      disabled={option.disabled}
                      as={Fragment}
                    >
                      {({ active, selected }) =>
                        menuItemRender ? (
                          menuItemRender({
                            option,
                            value,
                            onChange,
                            active,
                            selected,
                          })
                        ) : (
                          <div
                            className={clsx(
                              (active || selected) && !option.disabled
                                ? "bg-cosmic-3 font-semibold"
                                : "hover:bg-cosmic-4",
                              option.disabled &&
                                "bg-gray-3 pointer-events-none",
                              "px-4 py-2 cursor-pointer text-interstellar font-normal",
                            )}
                            dangerouslySetInnerHTML={createSearchHighlightedLabel(
                              option.label,
                              search,
                            )}
                          />
                        )
                      }
                    </ComboboxOption>
                  ))}
              </ComboboxOptions>
            </Transition>
          </>
        )}
      </Combobox>
    </div>
  );
}
